import { FunnelIcon } from "@heroicons/react/24/outline";
import { memo, useEffect, useRef, useState } from "react";
import { FilterPill } from "@/core/components/filter-pill";
import { SearchInput } from "@/core/components/search-input";
import {
  IconSelect,
  IconSelectContent,
  IconSelectItem,
  IconSelectTrigger,
} from "@/core/components/ui/icon-select";
import { Stack } from "@/core/components/ui/stack";
import { PageHeader } from "@/business-settings/common/page-header";
import { cn } from "@/core/utils/utils";
import {
  SPIFF_RULE_EVENTS_LABELS,
  SPIFF_RULE_EVENTS_OPTIONS,
} from "@/spiff-rules/constants";
import { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { SPIFF_EVENT_HEADER_COLORS } from "@/spiffs/constants";
import { ActivityFeedHelpCard } from "./activity-feed-help-card";
import { UnattributedReviewsFilterCard } from "./unattributed-reviews-filter-card";

interface ActivityFeedFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedFilters: string[];
  onFilterChange: (filters: string[]) => void;
  availableEventTypes: SpiffRuleEvent[];
  isPopoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  onFullWidthChange: (isFullWidth: boolean) => void;
  showUnattributedReviews: boolean;
  onToggleUnattributedReviews: () => void;
  hasUnattributedReviews: boolean;
  showSupportCards: boolean;
}

const FULL_WIDTH_BREAKPOINT = 1456;

export const ActivityFeedHeader = memo(
  ({
    searchValue,
    onSearchChange,
    selectedFilters,
    onFilterChange,
    availableEventTypes,
    isPopoverOpen,
    onPopoverOpenChange,
    onFullWidthChange,
    showUnattributedReviews,
    onToggleUnattributedReviews,
    hasUnattributedReviews,
    showSupportCards,
  }: ActivityFeedFiltersProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isAtFullWidth, setIsAtFullWidth] = useState(false);

    useEffect(() => {
      const checkWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          const atFullWidth = width >= FULL_WIDTH_BREAKPOINT;

          // Only update parent if the boolean state changed
          setIsAtFullWidth((prev) => {
            if (prev !== atFullWidth) {
              onFullWidthChange(atFullWidth);
              return atFullWidth;
            }
            return prev;
          });
        }
      };

      checkWidth();
      window.addEventListener("resize", checkWidth);
      return () => window.removeEventListener("resize", checkWidth);
    }, [onFullWidthChange]);

    const handleFilterChange = (filters: string[]) => {
      if (showUnattributedReviews) {
        onToggleUnattributedReviews();
      }
      if (filters.includes("all")) {
        onFilterChange([]);
        onPopoverOpenChange(false);
      } else {
        onFilterChange(filters);
      }
    };

    const handleRemoveFilter = (eventType: string) => {
      onFilterChange(selectedFilters.filter((f) => f !== eventType));
    };

    return (
      <div
        className={cn("top-0", {
          "pointer-events-none absolute left-0 right-0 z-30": isAtFullWidth,
          "sticky z-10 bg-gray-5/50 backdrop-blur-sm": !isAtFullWidth,
        })}
      >
        <Stack
          ref={containerRef}
          gap="none"
          fullWidth
          className={cn("mx-auto w-full max-w-[1456px] px-8 pb-2 pt-4", {
            "pointer-events-none": isAtFullWidth,
          })}
        >
          <Stack
            direction="row"
            align="center"
            justify="between"
            fullWidth
            className={cn("h-[44px]", {
              "pointer-events-auto": isAtFullWidth,
            })}
          >
            <PageHeader>Activity Feed</PageHeader>
            <Stack direction="row" align="center" gap="sm">
              <SearchInput
                placeholder="Search activities"
                value={searchValue}
                onChange={onSearchChange}
                className="bg-white"
              />
              <IconSelect
                value={selectedFilters}
                onValueChange={handleFilterChange}
                open={isPopoverOpen}
                onOpenChange={onPopoverOpenChange}
              >
                <IconSelectTrigger isActive={selectedFilters.length > 0}>
                  <FunnelIcon className="size-4" />
                </IconSelectTrigger>
                <IconSelectContent align="end">
                  <IconSelectItem value="all">All Types</IconSelectItem>
                  {availableEventTypes.map((eventType) => (
                    <IconSelectItem key={eventType} value={eventType}>
                      {SPIFF_RULE_EVENTS_OPTIONS[eventType]}
                    </IconSelectItem>
                  ))}
                </IconSelectContent>
              </IconSelect>
            </Stack>
          </Stack>

          {/* Help Banner and Filter Pills Row */}
          {isAtFullWidth ? (
            <Stack
              direction="row"
              align="start"
              justify="between"
              fullWidth
              gap="md"
              className="mt-4"
            >
              {/* Cards on the left */}
              {showSupportCards && (
                <Stack gap="md" className="pointer-events-auto max-w-[330px]">
                  {hasUnattributedReviews && (
                    <UnattributedReviewsFilterCard
                      isHeaderAtFullWidth={isAtFullWidth}
                      onFilterClick={onToggleUnattributedReviews}
                      isActive={showUnattributedReviews}
                    />
                  )}
                  <ActivityFeedHelpCard isHeaderAtFullWidth={isAtFullWidth} />
                </Stack>
              )}

              {/* Filter Pills on the right */}
              {selectedFilters.length > 0 && (
                <div className="pointer-events-auto flex max-w-[350px] flex-wrap items-center justify-end gap-2">
                  {selectedFilters.map((eventType) => (
                    <FilterPill
                      key={eventType}
                      backgroundColor={
                        SPIFF_EVENT_HEADER_COLORS[
                          eventType as SpiffRuleEvent
                        ] || "bg-gray-10"
                      }
                      onClear={() => handleRemoveFilter(eventType)}
                      className="whitespace-nowrap"
                    >
                      {SPIFF_RULE_EVENTS_LABELS[eventType as SpiffRuleEvent]}
                    </FilterPill>
                  ))}
                </div>
              )}
            </Stack>
          ) : (
            /* Filter Pills only on narrow screens */
            selectedFilters.length > 0 && (
              <div className="mt-4 flex w-full flex-wrap items-center justify-end gap-2">
                {selectedFilters.map((eventType) => (
                  <FilterPill
                    key={eventType}
                    backgroundColor={
                      SPIFF_EVENT_HEADER_COLORS[eventType as SpiffRuleEvent] ||
                      "bg-gray-10"
                    }
                    onClear={() => handleRemoveFilter(eventType)}
                    className="whitespace-nowrap"
                  >
                    {SPIFF_RULE_EVENTS_LABELS[eventType as SpiffRuleEvent]}
                  </FilterPill>
                ))}
              </div>
            )
          )}
        </Stack>
      </div>
    );
  },
);

ActivityFeedHeader.displayName = "ActivityFeedHeader";
